
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { SystemUserInterface, SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import { EditSystemUserForm } from '@/services/SystemUsers/forms/EditSystemUserForm';
import { FormValidationFunction } from '@/views/interfaces/vuetify/form.interface';
import { PasswordValidationCallbacks } from '@/services/misc/functions/ValidationFunctions';
import PageHeader from '@/components/layout/PageHeader.vue';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'systemUserRepository',
  ],
  components: {
    PageHeader,
  },
})
export default class EditSystemUser extends Vue {
  public form: WPXFormValue<EditSystemUserForm> = new WPXFormValue(EditSystemUserForm);
  public loading: boolean = true;

  public systemUserRepository!: SystemUserRepositoryInterface;
  public systemUserId!: number;

  public menu: string | false = false;

  public quotaMultipliers: { name: string, value: number }[] = [
    { name: 'KiB', value: 1 },
    { name: 'MiB', value: Math.pow(1024, 1) },
    { name: 'GiB', value: Math.pow(1024, 2) },
  ];

  public rules: { [key: string]: FormValidationFunction[] } = {
    quota: [
      (v: number) => !!v || 'Quota is required',
      (v: number) => v >= 0 || 'Quota must be equal or more than 0',
      (v: number) => {
        const quotaLimit = 200 * Math.pow(1024, 2);
        const currentQuota = v * this.form.data.quotaMultiplier;
        return currentQuota <= quotaLimit || `Quota can not exceed ${quotaLimit / this.form.data.quotaMultiplier}`;
      },
    ],
    password: [
      ...PasswordValidationCallbacks,
    ],
  };

  public async created(): Promise<void> {
    try {
      this.systemUserId = Number(this.$route.params.systemUserId);
      const systemUser = await this.systemUserRepository.findSystemUserById(this.systemUserId);
      await this.assignFormData(systemUser);
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public async editSystemUser(setQuotaFromPlan: boolean = false): Promise<void> {
    try {
      await this.systemUserRepository.editSystemUser(
          this.systemUserId,
          {
            password: this.form.data.password,
            passwordConf: this.form.data.passwordConf,
            quota: {
              size: this.form.data.quota * this.form.data.quotaMultiplier,
            },
            expireDate: this.form.data.expireDate,
            setQuotaFromPlan,
          },
      );
      this.$successDialog.show('System User Edited Successfully');
      await this.$router.push({ name: 'system_users_list' });
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public async assignFormData(systemUser: SystemUserInterface): Promise<void> {
    this.form.data = {
      ...this.form.data,
      quota: systemUser.quota.size,
      expireDate: systemUser.expireDate,
    };
  }
}
